import { useState, useEffect } from 'react'
import { useAtom } from 'jotai'
import { isAdminOrManager } from '../utils/roles'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Tabs,
  Tab,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Chip,
  IconButton,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tooltip,
  Stack,
} from '@mui/material'
import {
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  RestartAlt as ResetIcon,
  ExpandMore as ExpandMoreIcon,
  History as HistoryIcon,
  Code as CodeIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
} from '@mui/icons-material'
import { aiPromptsApi, AIPrompt, AIPromptCategory, ApiError, CreateAIPromptInput } from '../services/api'
import { useNotification } from '../hooks/useNotification'
import { userAtom } from '../state/auth'
import PageHelpButton from '../components/PageHelpButton'

// The Output Format field accepts EITHER a JSON schema OR a free-text instruction
// (e.g. north_star_generation's 'DYNAMIC' sentinel, or content_improvement's
// "Same JSON structure as the original content being improved"). Only validate as
// JSON when the value actually looks like JSON; free-text passes through and is
// injected verbatim into the prompt at generation time (see buildPrompt in ai-prompts.mjs).
const looksLikeJson = (s: string) => {
  const t = s.trim()
  return t.startsWith('{') || t.startsWith('[')
}

const PROMPT_CATEGORIES: { id: AIPromptCategory; label: string }[] = [
  { id: 'resume', label: 'Resume' },
  { id: 'linkedin', label: 'LinkedIn' },
  { id: 'interview', label: 'Interview' },
  { id: 'proposal', label: 'Proposal' },
  { id: 'other', label: 'Other' },
]

const CATEGORY_LABELS: Record<string, string> = {
  resume: 'Resume',
  linkedin: 'LinkedIn',
  interview: 'Interview',
  proposal: 'Proposal',
  other: 'Other',
}

export default function AIPrompts() {
  const [user] = useAtom(userAtom)
  const { showSuccess, showError } = useNotification()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [prompts, setPrompts] = useState<AIPrompt[]>([])
  const [activeTab, setActiveTab] = useState(0)
  const [error, setError] = useState<string | null>(null)

  // Dialog states
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [editingPrompt, setEditingPrompt] = useState<AIPrompt | null>(null)
  const [editForm, setEditForm] = useState<CreateAIPromptInput>({
    promptId: '',
    name: '',
    description: '',
    category: 'other',
    systemPrompt: '',
    instructions: '',
    outputFormat: '',
    model: 'claude-sonnet-5',
    maxTokens: 4000,
    isActive: true,
  })
  const [outputFormatError, setOutputFormatError] = useState<string | null>(null)

  // History dialog
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false)
  const [historyPrompt, setHistoryPrompt] = useState<AIPrompt | null>(null)

  const isAdmin = isAdminOrManager(user)

  const currentCategory = PROMPT_CATEGORIES[activeTab]?.id
  const filteredPrompts = prompts.filter((p) => p.category === currentCategory)

  useEffect(() => {
    loadPrompts()
  }, [])

  const loadPrompts = async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await aiPromptsApi.list()
      setPrompts(data)
    } catch (err) {
      console.error('Failed to load AI prompts:', err)
      setError('Failed to load AI prompts')
    } finally {
      setLoading(false)
    }
  }

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue)
  }

  const handleEditPrompt = (prompt: AIPrompt) => {
    setEditingPrompt(prompt)
    const outputFormatStr = typeof prompt.outputFormat === 'string'
      ? prompt.outputFormat
      : JSON.stringify(prompt.outputFormat, null, 2)
    setEditForm({
      promptId: prompt.promptId,
      name: prompt.name,
      description: prompt.description,
      category: prompt.category,
      systemPrompt: prompt.systemPrompt,
      instructions: prompt.instructions,
      outputFormat: outputFormatStr,
      model: prompt.model,
      maxTokens: prompt.maxTokens,
      isActive: prompt.isActive,
    })
    setOutputFormatError(null)
    setEditDialogOpen(true)
  }

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false)
    setEditingPrompt(null)
    setEditForm({
      promptId: '',
      name: '',
      description: '',
      category: 'other',
      systemPrompt: '',
      instructions: '',
      outputFormat: '',
      model: 'claude-sonnet-5',
      maxTokens: 4000,
      isActive: true,
    })
    setOutputFormatError(null)
  }

  const handleSavePrompt = async () => {
    if (!editForm.systemPrompt.trim() || !editForm.instructions.trim()) {
      showError('System prompt and instructions are required')
      return
    }

    // Output Format may be a JSON schema OR a free-text instruction (e.g. the
    // 'DYNAMIC' sentinel, or "Same JSON structure as the original content..." for
    // content_improvement). Only enforce valid JSON when the value looks like JSON;
    // free-text is injected verbatim into the prompt at generation time.
    const trimmedOutputFormat = editForm.outputFormat?.trim()
    if (trimmedOutputFormat && looksLikeJson(trimmedOutputFormat)) {
      try {
        JSON.parse(trimmedOutputFormat)
      } catch {
        showError('Output Format must be valid JSON')
        setOutputFormatError('Invalid JSON')
        return
      }
    }

    try {
      setSaving(true)

      // Only send editable fields - variables and other protected fields are managed by backend
      const updatedPrompt = await aiPromptsApi.update({
        promptId: editForm.promptId,
        name: editForm.name,
        description: editForm.description,
        category: editForm.category,
        systemPrompt: editForm.systemPrompt,
        instructions: editForm.instructions,
        outputFormat: editForm.outputFormat || undefined,
        model: editForm.model,
        maxTokens: editForm.maxTokens,
        isActive: editForm.isActive,
      })

      setPrompts((prev) =>
        prev.map((p) => (p.promptId === updatedPrompt.promptId ? updatedPrompt : p))
      )

      showSuccess('Prompt saved.')
      handleCloseEditDialog()
    } catch (err) {
      console.error('Failed to save prompt:', err)
      showError(err instanceof ApiError ? err.message : 'Failed to save prompt')
    } finally {
      setSaving(false)
    }
  }

  const handleResetPrompt = async (promptId: string) => {
    if (!confirm('This will reset the prompt to its default and delete any customizations. It cannot be undone.')) {
      return
    }

    try {
      setSaving(true)
      await aiPromptsApi.resetToDefault(promptId)
      await loadPrompts() // Reload to get the default prompt
      showSuccess('Prompt reset to default')
    } catch (err) {
      console.error('Failed to reset prompt:', err)
      showError('Failed to reset prompt')
    } finally {
      setSaving(false)
    }
  }

  const handleViewHistory = (prompt: AIPrompt) => {
    setHistoryPrompt(prompt)
    setHistoryDialogOpen(true)
  }

  if (!isAdmin) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography variant="h5" color="text.secondary">
          You don&apos;t have permission to manage AI prompts.
        </Typography>
      </Box>
    )
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>AI Prompts</Typography>
            <PageHelpButton articleId="admin-ai-prompts-configure" />
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Customize the prompts used by AI to generate content
          </Typography>
        </Box>
        <IconButton onClick={loadPrompts} disabled={loading}>
          <RefreshIcon />
        </IconButton>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent sx={{ p: 0 }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            {PROMPT_CATEGORIES.map((category, index) => {
              const count = prompts.filter((p) => p.category === category.id).length
              return (
                <Tab
                  key={category.id}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {category.label}
                      {count > 0 && (
                        <Chip
                          label={count}
                          size="small"
                          sx={{
                            height: 20,
                            minWidth: 20,
                            fontSize: '0.7rem',
                            backgroundColor: 'grey.200',
                          }}
                        />
                      )}
                    </Box>
                  }
                  value={index}
                />
              )
            })}
          </Tabs>

          <Box sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              {CATEGORY_LABELS[currentCategory]} Prompts
            </Typography>

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : filteredPrompts.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography color="text.secondary">
                  No prompts found in this category.
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {filteredPrompts.map((prompt) => (
                  <Accordion key={prompt.promptId} defaultExpanded={filteredPrompts.length === 1}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', pr: 2 }}>
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                            {prompt.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {prompt.description}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {prompt.createdBy && (
                            <Chip
                              label={`v${prompt.version}`}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          )}
                          {!prompt.isActive && (
                            <Chip
                              label="Inactive"
                              size="small"
                              color="warning"
                            />
                          )}
                        </Box>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {/* Editable Section */}
                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <LockOpenIcon sx={{ fontSize: 16, color: 'success.main' }} />
                            <Typography variant="subtitle2" color="success.main">
                              Editable Fields
                            </Typography>
                            <Tooltip title="You can customize these fields to change how the AI generates content">
                              <Chip label="Safe to edit" size="small" color="success" variant="outlined" />
                            </Tooltip>
                          </Box>

                          <Paper variant="outlined" sx={{ p: 2, mb: 2, borderColor: 'success.light' }}>
                            <Typography variant="subtitle2" color="primary" gutterBottom>
                              System Prompt (AI Personality)
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                fontFamily: 'monospace',
                                whiteSpace: 'pre-wrap',
                                backgroundColor: 'grey.50',
                                p: 1.5,
                                borderRadius: 1,
                                maxHeight: 200,
                                overflow: 'auto',
                              }}
                            >
                              {prompt.systemPrompt}
                            </Typography>
                          </Paper>

                          <Paper variant="outlined" sx={{ p: 2, mb: 2, borderColor: 'success.light' }}>
                            <Typography variant="subtitle2" color="primary" gutterBottom>
                              Output Format (JSON Schema)
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                fontFamily: 'monospace',
                                whiteSpace: 'pre-wrap',
                                backgroundColor: 'grey.50',
                                p: 1.5,
                                borderRadius: 1,
                                maxHeight: 200,
                                overflow: 'auto',
                              }}
                            >
                              {typeof prompt.outputFormat === 'string'
                                ? prompt.outputFormat
                                : JSON.stringify(prompt.outputFormat, null, 2)}
                            </Typography>
                          </Paper>

                          <Paper variant="outlined" sx={{ p: 2, borderColor: 'success.light' }}>
                            <Typography variant="subtitle2" color="primary" gutterBottom>
                              Instructions (Generation Guidance)
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                fontFamily: 'monospace',
                                whiteSpace: 'pre-wrap',
                                backgroundColor: 'grey.50',
                                p: 1.5,
                                borderRadius: 1,
                                maxHeight: 300,
                                overflow: 'auto',
                              }}
                            >
                              {prompt.instructions}
                            </Typography>
                          </Paper>
                        </Box>

                        <Divider />

                        {/* Protected Section */}
                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <LockIcon sx={{ fontSize: 16, color: 'warning.main' }} />
                            <Typography variant="subtitle2" color="warning.main">
                              Protected Fields (Read-Only)
                            </Typography>
                            <Tooltip title="These fields are set in code. The data template and variables define the app's data bindings, and the token ceiling is an engineering limit.">
                              <Chip label="Cannot edit" size="small" color="warning" variant="outlined" />
                            </Tooltip>
                          </Box>

                          <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                              Max Tokens
                            </Typography>
                            <Typography variant="body2">{prompt.maxTokens}</Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              A response ceiling is an engineering limit, so it is set in code rather than here.
                            </Typography>
                          </Box>

                          <Paper variant="outlined" sx={{ p: 2, mb: 2, borderColor: 'warning.light', backgroundColor: 'grey.50' }}>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                              Data Template (Input Variables)
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                fontFamily: 'monospace',
                                whiteSpace: 'pre-wrap',
                                backgroundColor: 'white',
                                p: 1.5,
                                borderRadius: 1,
                                maxHeight: 200,
                                overflow: 'auto',
                              }}
                            >
                              {prompt.dataTemplate}
                            </Typography>
                          </Paper>

                          {prompt.variables.length > 0 && (
                            <Box>
                              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                System Variables (Auto-populated)
                              </Typography>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {prompt.variables.map((variable) => (
                                  <Chip
                                    key={variable}
                                    icon={<CodeIcon sx={{ fontSize: 14 }} />}
                                    label={`{{${variable}}}`}
                                    size="small"
                                    variant="outlined"
                                    color="default"
                                  />
                                ))}
                              </Box>
                            </Box>
                          )}
                        </Box>

                        <Divider />

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Chip
                              label={`Model: ${prompt.model}`}
                              size="small"
                              variant="outlined"
                            />
                          </Box>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            {prompt.history && prompt.history.length > 0 && (
                              <Button
                                size="small"
                                startIcon={<HistoryIcon />}
                                onClick={() => handleViewHistory(prompt)}
                              >
                                History ({prompt.history.length})
                              </Button>
                            )}
                            {!prompt.isDefault && (
                              <Button
                                size="small"
                                color="warning"
                                startIcon={<ResetIcon />}
                                onClick={() => handleResetPrompt(prompt.promptId)}
                                disabled={saving}
                              >
                                Reset
                              </Button>
                            )}
                            <Button
                              size="small"
                              variant="contained"
                              startIcon={<EditIcon />}
                              onClick={() => handleEditPrompt(prompt)}
                              disabled={saving}
                            >
                              Edit
                            </Button>
                          </Box>
                        </Box>

                        {prompt.updatedBy && (
                          <Typography variant="caption" color="text.secondary">
                            Last updated by {prompt.updatedBy} on{' '}
                            {new Date(prompt.updatedAt!).toLocaleString()}
                          </Typography>
                        )}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Edit Prompt Dialog */}
      <Dialog open={editDialogOpen} onClose={handleCloseEditDialog} maxWidth="md" fullWidth>
        <DialogTitle>Edit Prompt: {editingPrompt?.name}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Name"
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                fullWidth
              />
              <FormControl sx={{ minWidth: 150 }}>
                <InputLabel>Category</InputLabel>
                <Select
                  value={editForm.category}
                  label="Category"
                  onChange={(e) => setEditForm({ ...editForm, category: e.target.value as AIPromptCategory })}
                >
                  {PROMPT_CATEGORIES.map((cat) => (
                    <MenuItem key={cat.id} value={cat.id}>
                      {cat.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <TextField
              label="Description"
              value={editForm.description}
              onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
              fullWidth
              multiline
              rows={2}
            />

            <Alert severity="info" sx={{ mb: 1 }}>
              You can customize the AI personality, generation instructions, and output format below. Protected fields (data template, variables, max tokens) are set in code and cannot be changed here.
            </Alert>

            <TextField
              label="System Prompt (AI Personality)"
              value={editForm.systemPrompt}
              onChange={(e) => setEditForm({ ...editForm, systemPrompt: e.target.value })}
              fullWidth
              multiline
              rows={6}
              required
              placeholder="Describe the AI's role, expertise, and approach..."
              helperText="Defines who the AI is and how it should behave"
              InputProps={{
                sx: { fontFamily: 'monospace', fontSize: '0.875rem' },
              }}
            />

            <TextField
              label="Instructions (Generation Guidance)"
              value={editForm.instructions}
              onChange={(e) => setEditForm({ ...editForm, instructions: e.target.value })}
              fullWidth
              multiline
              rows={10}
              required
              placeholder="Provide guidelines for generating content..."
              helperText="Guidelines for tone, style, quality criteria, and what to include"
              InputProps={{
                sx: { fontFamily: 'monospace', fontSize: '0.875rem' },
              }}
            />

            <TextField
              label="Output Format (JSON Schema)"
              value={editForm.outputFormat}
              onChange={(e) => {
                const val = e.target.value
                setEditForm({ ...editForm, outputFormat: val })
                const trimmed = val.trim()
                // Only validate when it looks like JSON — free-text instructions
                // (incl. the 'DYNAMIC' sentinel) are allowed. See handleSavePrompt.
                if (trimmed && looksLikeJson(trimmed)) {
                  try {
                    JSON.parse(val)
                    setOutputFormatError(null)
                  } catch {
                    setOutputFormatError('Invalid JSON')
                  }
                } else {
                  setOutputFormatError(null)
                }
              }}
              fullWidth
              multiline
              rows={8}
              error={!!outputFormatError}
              helperText={outputFormatError || 'A JSON schema (validated as JSON), or a free-text instruction such as "DYNAMIC" or "Same structure as the input".'}
              InputProps={{
                sx: { fontFamily: 'monospace', fontSize: '0.875rem' },
              }}
            />

            {/* MWA-325 — Max Tokens is not editable here. getPromptConfig always
                takes it from DEFAULT_PROMPTS, so an edit box would have saved a
                value that generation then ignored. editForm still carries the
                stored value so saving round-trips it unchanged. */}
            <TextField
              label="Model"
              value={editForm.model}
              onChange={(e) => setEditForm({ ...editForm, model: e.target.value })}
              fullWidth
            />

            <FormControlLabel
              control={
                <Switch
                  checked={editForm.isActive}
                  onChange={(e) => setEditForm({ ...editForm, isActive: e.target.checked })}
                />
              }
              label="Active (use this prompt for generation)"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseEditDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSavePrompt}
            disabled={saving || !editForm.systemPrompt.trim() || !editForm.instructions.trim() || !!outputFormatError}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* History Dialog */}
      <Dialog open={historyDialogOpen} onClose={() => setHistoryDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Version History: {historyPrompt?.name}</DialogTitle>
        <DialogContent>
          {historyPrompt?.history && historyPrompt.history.length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {historyPrompt.history.map((version, index) => (
                <Accordion key={index}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Chip label={`v${version.version}`} size="small" color="primary" variant="outlined" />
                      <Typography variant="body2">
                        Updated by {version.updatedBy} on {new Date(version.updatedAt).toLocaleString()}
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Box>
                        <Typography variant="subtitle2" color="primary" gutterBottom>
                          System Prompt
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: 'monospace',
                            whiteSpace: 'pre-wrap',
                            backgroundColor: 'grey.50',
                            p: 1.5,
                            borderRadius: 1,
                            maxHeight: 150,
                            overflow: 'auto',
                          }}
                        >
                          {version.systemPrompt}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" color="primary" gutterBottom>
                          Instructions
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: 'monospace',
                            whiteSpace: 'pre-wrap',
                            backgroundColor: 'grey.50',
                            p: 1.5,
                            borderRadius: 1,
                            maxHeight: 200,
                            overflow: 'auto',
                          }}
                        >
                          {version.instructions}
                        </Typography>
                      </Box>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          ) : (
            <Typography color="text.secondary">No version history available.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
